import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Stack,
  useTheme,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import SaveIcon from '@mui/icons-material/Save';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { SelectChangeEvent } from '@mui/material/Select';

import { FullEvent, EventDescription } from "../../shared/FullEvent";
import { useEventsApi } from '../../hooks/useEventsApi';
import ApiErrorDialog, { type ApiErrorDialogContent } from "../../components/ApiErrorDialog";
import CustomAlert from "../../components/CustomAlert"
import { config } from "../../config"
import { describeApiError } from "../../utils/apiErrorMessage";
import { formatLocalDateTime } from "../../utils/formatLocalDateTime";
import getUserRoleFlags from '../../hooks/useGetUserRoleFlags';
import ApproveEventButton from '../../components/ApproveEventButton';
import { ContractRenameDialog } from "../../components/ContractRenameDialog";

import { EventBaseInfo } from "./EventBaseInfo";
import { EventDescriptions, SimilarEventHint } from "./EventDescriptions";
import { EventParameters, type EventParameter } from "./EventParameters";
import { EventVersionInfo } from "./EventVersionInfo";
import {
  creatorSourceLanguageSelectLabels,
  eventLanguageSortRank,
  normalizeEventLanguage,
  supportedEventLanguages,
  type EventLanguageCode,
} from "../../constants/languages";
import { normalizeTranslationReviewMessage } from "../../constants/translationReview";
import EventsFormDialog from "../../components/EventsFormDialog";
import EventVehicleSoftwareScanAccordion from "../../components/EventVehicleSoftwareScanAccordion";
import {
  captureDevCommentBaseline,
  isOnlyDevCommentChanged,
  type DevCommentBaseline,
} from "../../utils/eventContentFingerprint";

export interface EventsFormSavedPayload {
  eventId?: number | null;
  eventCode?: string;
  approved?: boolean;
  created?: boolean; // z.B. bei createNewEvent
}

interface EventsFormProps {
  eventId?: number | null;
  onSaved?: (payload: EventsFormSavedPayload) => void;
  createNewEvent: boolean;
  configuratorMode: boolean;
  data?: EventsFormData;
  isFallingEvent?: boolean;
  onDataChange?: (data: EventsFormData) => void;

  /** NEU: Parent erfährt, ob alle Pflichtfelder erfüllt sind */
  onValidChange?: (valid: boolean) => void;
  /** Parent can show unsaved-changes warning on dialog close/navigation. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Force read-only mode independent of current role. */
  forceReadOnly?: boolean;
  /** Creator resolving step: contract name from the rising event (display only). */
  creatorRisingContractName?: string | null;
}

// Rising event
export type EventsFormData = FullEvent;
type EventDescriptionsData = EventDescription[];

type ValidationErrors = Record<string, string>;

const InfoIconMemo = React.memo(InfoOutlineIcon);

const formatDate = (datum: Date): string => {
  const jahr = datum.getFullYear();
  const monat = String(datum.getMonth() + 1).padStart(2, '0');
  const tag = String(datum.getDate()).padStart(2, '0');
  return `${jahr}-${monat}-${tag}`;
};

const today = new Date();
const formattedDate = formatDate(today);

const initialDescriptions: EventDescriptionsData = [
  { id: 0, global_event: 0, language_code: 'ger', label: '', label_long: '', description: '', situation: '', actions: [''] },
  { id: 0, global_event: 0, language_code: 'eng', label: '', label_long: '', description: '', situation: '', actions: [''] },
  { id: 0, global_event: 0, language_code: 'ita', label: '', label_long: '', description: '', situation: '', actions: [''] },
  { id: 0, global_event: 0, language_code: 'cze', label: '', label_long: '', description: '', situation: '', actions: [''] },
];

const initialFormData: EventsFormData = {
  global_event_id: 0,
  approved_at: null,
  event_type: "UserEvent",
  event_code: 0,
  plant_specific: false,
  attributes: {
    severity: "",
    source: "",
    event_code_old: 0,
    alarm: false,
    message_history: false,
    manufacturer_event: false,
    creator_source_language: "ger",
    parameters: [
      { parameter_id: 1, parameter_contents: 'Segment', parameter_type: 'Segment ID' },
      { parameter_id: 2, parameter_contents: '', parameter_type: ' ' },
      { parameter_id: 3, parameter_contents: '', parameter_type: ' ' },
      { parameter_id: 4, parameter_contents: '', parameter_type: ' ' },
    ],
  },
  event_descriptions: initialDescriptions,
  approved_by: "",
  version: 0,
  event_contract_name: "",
  module: 10,
};

/** Empty creator wizard payload (e.g. reset resolving-only rising link). */
export const creatorInitialFormData: EventsFormData = initialFormData;

const SYSTEM_PARAMETER_ONE_ID = 1;

/** First parameter row is fixed to Segment / Segment ID for PLC integration. */
function normalizeSystemParameterOne(parameters: EventParameter[] | undefined): EventParameter[] {
  const base =
    parameters?.length && parameters.length > 0
      ? parameters.map((p) => ({ ...p }))
      : (initialFormData.attributes?.parameters ?? []).map((p) => ({ ...p }));
  return base.map((p) =>
    Number(p.parameter_id) === SYSTEM_PARAMETER_ONE_ID
      ? { ...p, parameter_contents: "Segment", parameter_type: "Segment ID" }
      : p
  );
}

const nonEmpty = (v: unknown) =>
  typeof v === "string"
    ? v.trim() !== "" && v.trim().toLowerCase() !== "null" && v.trim().toLowerCase() !== "undefined"
    : v !== null && v !== undefined;

/** PascalCase English identifier (no spaces); used for VDA 5050 and cross-system naming. */
const EVENT_CONTRACT_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9]*$/;

function validateEventContractName(value: unknown): string | null {
  const raw = String(value ?? "");
  const trimmed = raw.trim();
  if (!trimmed) {
    return "Required";
  }
  if (/\s/.test(raw)) {
    return "Must not contain spaces";
  }
  if (!EVENT_CONTRACT_NAME_PATTERN.test(trimmed)) {
    return "Use English letters only (PascalCase), e.g. ChargingContactorOpened";
  }
  return null;
}

/** Draft rows (no approval timestamp) stay at version 0; bumps apply only after approval. */
function hasApprovedAt(formData: Pick<EventsFormData, "approved_at">): boolean {
  const at = formData?.approved_at;
  if (typeof at !== "string") {
    return false;
  }
  const t = at.trim();
  return (
    t !== "" &&
    t.toLowerCase() !== "null" &&
    t.toLowerCase() !== "undefined"
  );
}

const hasMeaningfulDescriptionContent = (desc: EventDescription): boolean => {
  const hasText =
    nonEmpty(desc.label) ||
    nonEmpty(desc.label_long) ||
    nonEmpty(desc.description) ||
    nonEmpty(desc.situation);
  const hasActions = Array.isArray(desc.actions) && desc.actions.some((a) => nonEmpty(a));
  return Boolean(hasText || hasActions);
};

const sanitizeDescriptionsForCreator = (
  descriptions: EventDescription[],
  primaryLang: string
): EventDescription[] => {
  const primary = normalizeEventLanguage(primaryLang) || "ger";
  return (descriptions ?? []).filter((desc) => {
    const lang = normalizeEventLanguage(desc.language_code);
    if (lang === primary) {
      return true;
    }
    return hasMeaningfulDescriptionContent(desc);
  });
};

const areValidationErrorsEqual = (a: ValidationErrors, b: ValidationErrors): boolean => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
};

/**
 * Validierung spiegelt die UI:
 * - severity immer required
 * - event_contract_name im Creator required (rising + resolving), PascalCase English, no spaces
 * - event_type und event_code nur außerhalb des Configurators required
 * - description & situation nicht required in [10000, 20000)
 * - mindestens ger & eng prüfen (label/label_long immer, desc/situation je nach Eventcodebereich)
 */
function validateEventsForm(
  formData: EventsFormData,
  options: { configuratorMode: boolean },
  isFallingEvent?: boolean
): { valid: boolean; errors: ValidationErrors } {
  const { configuratorMode } = options;
  const errors: ValidationErrors = {};

  const requiresTextBlocks = !(formData.event_code >= 10000 && formData.event_code < 20000);

  // --- Sichtbare Basis-Felder (nur außerhalb des Configurators) ---
  if (!configuratorMode) {
    if (!nonEmpty(formData.event_type)) {
      errors["event_type"] = "Required";
    }
    if (formData.event_code === null || formData.event_code === undefined || Number.isNaN(Number(formData.event_code))) {
      errors["event_code"] = "Required";
    }
  }

  // --- Creator: contract name required on rising and resolving steps ---
  if (configuratorMode) {
    const contractErr = validateEventContractName(formData.event_contract_name);
    if (contractErr) {
      errors["event_contract_name"] = contractErr;
    }
  }

  // --- Severity ist immer required ---
  if (!nonEmpty(formData.attributes?.severity)) {
    errors["attributes.severity"] = "Required";
  }

  // --- event_descriptions: im Creator nur gewählte Primärsprache, sonst ger+eng ---
  const creatorPrimary = normalizeEventLanguage(formData.attributes?.creator_source_language || "ger");
  const wantedLangs = configuratorMode ? new Set([creatorPrimary]) : new Set(["ger", "eng"]);
  const descByLang = new Map<string, EventDescription>(
    (formData.event_descriptions ?? []).map(d => [normalizeEventLanguage(d.language_code), d])
  );

  for (const lang of wantedLangs) {
    const desc = descByLang.get(lang);
    if (!desc) {
      errors[`desc.${lang}`] = `Missing ${lang} description block`;
      continue;
    }
    if (!nonEmpty(desc.label)) {
      errors[`desc.${lang}.label`] = "Required";
    }
    if (!nonEmpty(desc.label_long)) {
      errors[`desc.${lang}.label_long`] = "Required";
    }
    if (requiresTextBlocks) {
      if (!nonEmpty(desc.description) && !isFallingEvent) {
        errors[`desc.${lang}.description`] = "Required (for this event code range)";
      }
      if (!nonEmpty(desc.situation) && !isFallingEvent) {
        errors[`desc.${lang}.situation`] = "Required (for this event code range)";
      }
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

const EventsForm: React.FC<EventsFormProps> = ({
  eventId,
  onSaved,
  createNewEvent,
  configuratorMode,
  data,
  isFallingEvent,
  onDataChange,
  onValidChange,
  onDirtyChange,
  forceReadOnly = false,
  creatorRisingContractName = null,
}) => {
  const isNonEmptyEventsFormData = (d: any): d is EventsFormData => {
    return !!d && typeof d === "object" &&
      Array.isArray(d.event_descriptions) &&
      d.event_descriptions.length >= 1;
  };

  const [allowSave, setAllowSave] = React.useState(false);

  const mergeWithDefaults = (incoming?: Partial<EventsFormData>): EventsFormData => ({
    ...initialFormData,
    ...(incoming ?? {}),
    attributes: {
      ...initialFormData.attributes,
      ...(incoming?.attributes ?? {}),
      creator_source_language:
        incoming?.attributes?.creator_source_language ??
        initialFormData.attributes?.creator_source_language,
      parameters: normalizeSystemParameterOne(
        incoming?.attributes?.parameters?.length
          ? incoming.attributes.parameters
          : initialFormData.attributes?.parameters
      ),
    },
    event_descriptions: incoming?.event_descriptions?.length
      ? incoming.event_descriptions
      : initialFormData.event_descriptions,
  });

  const initialMergedData: EventsFormData =
    isNonEmptyEventsFormData(data) ? mergeWithDefaults(data) : initialFormData;
  const [formData, setFormData] = useState<EventsFormData>(initialMergedData);
  const dirtyBaselineRef = useRef<string>(JSON.stringify(initialMergedData));
  const dirtyBaselineDataRef = useRef<EventsFormData>(
    JSON.parse(JSON.stringify(initialMergedData)) as EventsFormData
  );
  const [isDirtyLocal, setIsDirtyLocal] = useState(false);
  const [contractRenameOpen, setContractRenameOpen] = useState(false);

  const devCommentBaselineRef = useRef<DevCommentBaseline | null>(null);
  const [resolveDevCommentBusy, setResolveDevCommentBusy] = useState(false);

  const [errors, setErrors] = useState<ValidationErrors>({});

  const resetDirtyBaseline = useCallback((next: EventsFormData) => {
    const s = JSON.stringify(next);
    dirtyBaselineRef.current = s;
    dirtyBaselineDataRef.current = JSON.parse(JSON.stringify(next)) as EventsFormData;
    setIsDirtyLocal(false);
    onDirtyChange?.(false);
  }, [onDirtyChange]);

  const handleUndoChanges = () => {
    setFormData(JSON.parse(JSON.stringify(dirtyBaselineDataRef.current)) as EventsFormData);
    setIsDirtyLocal(false);
    onDirtyChange?.(false);
  };

  const handleRisingEventChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const nextValue = name === "event_contract_name" ? value.replace(/\s+/g, "") : value;
    setIsDirtyLocal(true);
    setFormData(prev => ({ ...prev, [name]: nextValue }));
  };

  const handleAttributeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name } = e.target;
    setIsDirtyLocal(true);
    const nextValue =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.type === "number"
          ? (e.target.value === "" ? null : Number(e.target.value))
          : e.target.type === "date"
            ? (e.target.value || null)
            : e.target.value;

    setFormData(prev => ({
      ...prev,
      attributes: {
        ...(prev.attributes ?? {}),
        [name]: nextValue,
      },
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setIsDirtyLocal(true);
    setFormData(prev => ({ ...prev, [name as keyof EventsFormData]: value }));
  };

  const handleAttributeSelectChange = (e: SelectChangeEvent) => {
    const { value } = e.target;
    // Only the Severity field uses this handler. MUI Select sometimes omits `target.name`; default it so
    // the value always updates `attributes.severity` (otherwise it lands on `attributes["undefined"]` and the
    // creator accordion lock never clears).
    const rawName = (e.target as { name?: string }).name;
    const name = rawName && String(rawName).trim() !== "" ? rawName : "severity";
    setIsDirtyLocal(true);
    setFormData((prev) => ({
      ...prev,
      attributes: {
        ...(prev.attributes ?? {}),
        [name]: value,
      },
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setIsDirtyLocal(true);
    setFormData(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [name]: checked,
      },
    }));
  };

  const handleReset = () => {
    setFormData(initialFormData);
    resetDirtyBaseline(initialFormData);
  };

  const defaultDescFor = (lang: string): EventDescription => ({
    id: 0,
    global_event: 0,
    language_code: lang,
    label: "",
    label_long: "",
    description: "",
    situation: "",
    actions: [""],
  });

  const handleDescriptionsChangeRising = useCallback((
    eOrActions:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | string[],
    language: string
  ) => {
    setIsDirtyLocal(true);
    setFormData(prev => {
      const prevDescs = prev.event_descriptions ?? [];
      const normalizedLanguage = normalizeEventLanguage(language);
      const idx = prevDescs.findIndex(
        d => normalizeEventLanguage(d.language_code) === normalizedLanguage
      );
      const current = idx >= 0 ? prevDescs[idx] : defaultDescFor(language);

      let next: EventDescription;

      if (Array.isArray(eOrActions)) {
        next = { ...current, actions: eOrActions as string[] };
      } else {
        const { name, value } = eOrActions.target as
          | HTMLInputElement
          | HTMLTextAreaElement;

        if (name !== "label" && name !== "label_long" && name !== "description" && name !== "situation") {
          return prev;
        }

        const currentValue = (current as any)[name];
        if (currentValue === value) {
          return prev;
        }
        next = { ...current, [name]: value } as EventDescription;
      }

      const newDescs =
        idx >= 0
          ? prevDescs.map((d, i) => (i === idx ? next : d))
          : [...prevDescs, next];

      return { ...prev, event_descriptions: newDescs };
    });
  }, []);

  const handleParametersChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id: number
  ) => {
    if (id === SYSTEM_PARAMETER_ONE_ID) {
      return;
    }
    const { name, value } = e.target;
    setIsDirtyLocal(true);

    setFormData(prev => {
      const updatedParams = prev.attributes?.parameters?.map(param =>
        param.parameter_id === id
          ? { ...param, [name]: value }
          : param
      ) || [];

      return {
        ...prev,
        attributes: {
          ...prev.attributes,
          parameters: updatedParams
        }
      };
    });
  }, []);

  const { callApi } = useEventsApi();

  const checkEventExists = async (event_code: number, event_type: string): Promise<boolean> => {
    const params = new URLSearchParams({
      event_code_exact: String(event_code),
      event_type: String(event_type),
      latest: "true",
      page_size: "5",
    });
    const res = await callApi(`${config.apiUrl}/api/events/?${params.toString()}`, "GET");
    const rows = Array.isArray(res) ? res : (res?.results ?? []);
    return rows.length > 0;
  };

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<React.ReactNode>("");
  const [alertSeverity, setAlertSeverity] = useState<"error" | "success" | "warning">("error");
  const [alertDuration, setAlertDuration] = useState(6000);
  const [errorDialog, setErrorDialog] = useState<ApiErrorDialogContent | null>(null);
  const [eventLoadBusy, setEventLoadBusy] = useState(() => Boolean(eventId));
  const [saveBusy, setSaveBusy] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [similarDialogEventId, setSimilarDialogEventId] = useState<number | null>(null);
  const [similarDialogOpen, setSimilarDialogOpen] = useState(false);
  const [translationStatus, setTranslationStatus] = useState<any>({ status: "idle" });
  const [trackedTranslationEventId, setTrackedTranslationEventId] = useState<number | null>(null);
  const [translationSynced, setTranslationSynced] = useState(false);
  const [translationPollActive, setTranslationPollActive] = useState(false);
  const [retranslateSourceLang, setRetranslateSourceLang] = useState<EventLanguageCode>("ger");
  const [retranslateForce, setRetranslateForce] = useState(false);
  const [retranslateBusy, setRetranslateBusy] = useState(false);
  const [retranslateDialogOpen, setRetranslateDialogOpen] = useState(false);
  const [retranslateTargetSelection, setRetranslateTargetSelection] = useState<
    Partial<Record<EventLanguageCode, boolean>>
  >({});

  const retranslateTargetCandidates = useMemo(
    () => supportedEventLanguages.filter((c) => c !== retranslateSourceLang),
    [retranslateSourceLang]
  );
  const { isAdmin, isCreator, isReader } = getUserRoleFlags();
  const effectiveIsReader = isReader || forceReadOnly;
  const onDataChangeRef = useRef(onDataChange);
  const onValidChangeRef = useRef(onValidChange);
  const lastSentDataRef = useRef<string>("");
  const lastValidRef = useRef<boolean | null>(null);

  const formatDateForInput = (isoString: string): string =>
    formatLocalDateTime(isoString);

  const effectiveGlobalEventId =
    Number(formData.global_event_id) > 0
      ? Number(formData.global_event_id)
      : eventId != null && Number(eventId) > 0
        ? Number(eventId)
        : 0;

  /** Undo is only meaningful while the form holds unsaved edits of an existing event. */
  const canUndoEdits = !createNewEvent && isDirtyLocal;

  const showApiErrorDialog = (
    title: string,
    summary: string,
    error: unknown,
    fallback: string,
    options?: { allowUndo?: boolean }
  ) => {
    const { messages, status } = describeApiError(error, fallback);
    setErrorDialog({ title, summary, messages, status, allowUndo: options?.allowUndo });
  };

  const showBlockingMessage = (title: string, summary: string, message: string) => {
    setErrorDialog({ title, summary, messages: [message] });
  };

  const handleApprove = async () => {
    try {
      setSaveBusy(true);
      const payload = { approved: true };
      const method = "PATCH";
      const url = `${config.apiUrl}/api/events/${effectiveGlobalEventId}/`;

      const risingEventRes = await callApi(url, method, payload);
      const risingEventCode = risingEventRes.event_code;

      onSaved?.({
        eventId: formData.global_event_id,
        eventCode: risingEventCode,
        approved: true,
      });
    } catch (error) {
      showApiErrorDialog(
        "Approval failed",
        "The event was not approved. The server reported:",
        error,
        "Error while approving."
      );
    } finally {
      setSaveBusy(false);
    }
  }

  const handleSubmit = async (options?: { closeAfterSave?: boolean }) => {
    const closeAfterSave = options?.closeAfterSave ?? true;
    if (!isDirtyLocal) {
      return;
    }
    try {
      setSaveBusy(true);

      if (
        !createNewEvent &&
        effectiveGlobalEventId > 0 &&
        isOnlyDevCommentChanged(formData, devCommentBaselineRef.current)
      ) {
        try {
          const patched = await callApi(
            `${config.apiUrl}/api/events/${effectiveGlobalEventId}/dev-comment/`,
            "PATCH",
            { dev_comment: formData.attributes?.dev_comment ?? "" },
          );
          if (patched && typeof patched === "object" && "global_event_id" in patched) {
            const merged = mergeWithDefaults(patched as Partial<EventsFormData>);
            setFormData(merged);
            resetDirtyBaseline(merged);
            devCommentBaselineRef.current = captureDevCommentBaseline(merged as FullEvent);
          }
          setAlertSeverity("success");
          setAlertDuration(6000);
          setAlertMessage("Development comment saved (event version unchanged).");
          setAlertOpen(true);
          if (closeAfterSave) {
            onSaved?.({
              eventId: formData.global_event_id,
              eventCode: String(formData.event_code ?? ""),
              approved: false,
            });
          }
        } catch (error) {
          showApiErrorDialog(
            "Saving failed",
            "The development comment was not saved. The server reported:",
            error,
            "Error while saving development comment.",
            { allowUndo: canUndoEdits }
          );
        } finally {
          setSaveBusy(false);
        }
        return;
      }

      // Always PATCH the existing row by id when editing. Approval status must not switch the
      // request to POST: that path runs create + duplicate check and breaks saves for approved
      // events (e.g. opened from Admin duplicate report vs unapproved-only approval backlog).
      const usePutRequest = Boolean(!createNewEvent && effectiveGlobalEventId > 0);
      let versionForPayload = formData.version ?? 0;

      if (createNewEvent && versionForPayload > 0) {
        showBlockingMessage(
          "Saving failed",
          "The event was not created:",
          `An event with code ${formData.event_code.toString()} already exists. To change it, use the Home page.`
        );
        return;
      }
      if (!createNewEvent && hasApprovedAt(formData)) {
        versionForPayload += 1;
      }

      if (!usePutRequest) {
        const exists = await checkEventExists(formData.event_code, formData.event_type);
        if (exists) {
          showBlockingMessage(
            "Saving failed",
            "The event was not created:",
            `An event of type ${formData.event_type} with code ${formData.event_code.toString()} already exists. Please change the event code.`
          );
          return;
        }
      }

      const primaryForSave = normalizeEventLanguage(formData.attributes?.creator_source_language || "ger");
      const payloadRisingEvent = {
        ...formData,
        version: versionForPayload,
        global_event_id: usePutRequest ? effectiveGlobalEventId : 0,
        ...(configuratorMode
          ? {
              attributes: {
                ...formData.attributes,
                alarm: false,
                message_history: false,
              },
            }
          : {}),
        event_descriptions: configuratorMode
          ? sanitizeDescriptionsForCreator(formData.event_descriptions ?? [], primaryForSave)
          : formData.event_descriptions,
        creator_mode: configuratorMode,
      };

      const method = usePutRequest ? "PUT" : "POST";
      const url = usePutRequest
        ? `${config.apiUrl}/api/events/${effectiveGlobalEventId}/`
        : `${config.apiUrl}/api/events/`;

      const risingEventRes = await callApi(url, method, payloadRisingEvent);
      if (!risingEventRes || typeof risingEventRes !== "object") {
        throw new Error("Save returned no event payload.");
      }
      const risingEventCode = risingEventRes.event_code;
      const savedEventId = Number(risingEventRes.global_event_id ?? 0) || null;
      if (
        risingEventRes &&
        typeof risingEventRes === "object" &&
        risingEventRes.global_event_id != null
      ) {
        const merged = mergeWithDefaults(risingEventRes as Partial<EventsFormData>);
        setFormData(merged);
        resetDirtyBaseline(merged);
        devCommentBaselineRef.current = captureDevCommentBaseline(merged as FullEvent);
      }
      if (configuratorMode && savedEventId) {
        setTrackedTranslationEventId(savedEventId);
        setTranslationSynced(false);
      }

      const reviewRequired = Boolean(
        risingEventRes &&
          typeof risingEventRes === "object" &&
          (risingEventRes as { translation_review_required?: boolean }).translation_review_required
      );
      const reviewMsg = normalizeTranslationReviewMessage(
        (risingEventRes as { translation_review_message?: string }).translation_review_message
      );

      if (reviewRequired) {
        setAlertSeverity("warning");
        setAlertDuration(22000);
        setAlertMessage(
          <Stack spacing={1.25}>
            <Typography variant="body2" fontWeight={700}>
              Event {risingEventCode} erfolgreich {!createNewEvent ? "aktualisiert" : "angelegt"}.
            </Typography>
            <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                Review translations
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {reviewMsg}
              </Typography>
            </Alert>
          </Stack>
        );
      } else {
        setAlertSeverity("success");
        setAlertDuration(6000);
        setAlertMessage(
          `Successfully ${!createNewEvent ? "updated" : "created"} event ${risingEventCode}!`
        );
      }
      setAlertOpen(true);
      if (closeAfterSave) {
        onSaved?.({
          eventId: savedEventId ?? formData.global_event_id,
          eventCode: risingEventCode,
          approved: false,
        });
      }
    } catch (error) {
      showApiErrorDialog(
        "Saving failed",
        `The event was not ${createNewEvent ? "created" : "updated"}. The server reported:`,
        error,
        "Error while saving.",
        { allowUndo: canUndoEdits }
      );
    } finally {
      setSaveBusy(false);
    }
  };

  const handleResolveDevComment = async () => {
    if (!formData.global_event_id || !isAdmin) return;
    try {
      setResolveDevCommentBusy(true);
      const patched = await callApi(
        `${config.apiUrl}/api/events/${formData.global_event_id}/dev-comment/`,
        "PATCH",
        { dev_comment: "" },
      );
      if (patched && typeof patched === "object" && "global_event_id" in patched) {
        const merged = mergeWithDefaults(patched as Partial<EventsFormData>);
        setFormData(merged);
        resetDirtyBaseline(merged);
        devCommentBaselineRef.current = captureDevCommentBaseline(merged as FullEvent);
      }
      setAlertSeverity("success");
      setAlertDuration(5000);
      setAlertMessage("Development comment cleared (event version unchanged).");
      setAlertOpen(true);
    } catch (error) {
      showApiErrorDialog(
        "Saving failed",
        "The development comment was not cleared. The server reported:",
        error,
        "Could not clear development comment."
      );
    } finally {
      setResolveDevCommentBusy(false);
    }
  };

  const handleDeleteHighestVersion = async () => {
    if (!isAdmin || !formData.global_event_id) return;
    try {
      setDeleteBusy(true);
      await callApi(`${config.apiUrl}/api/events/${formData.global_event_id}/`, "DELETE");
      setAlertSeverity("success");
      setAlertDuration(6000);
      setAlertMessage(`Successfully deleted highest version of event ${formData.event_code}.`);
      setAlertOpen(true);
      onSaved?.({
        eventId: formData.global_event_id,
        eventCode: String(formData.event_code ?? ""),
        approved: false,
      });
    } catch (error) {
      showApiErrorDialog(
        "Deletion failed",
        "The event version was not deleted. The server reported:",
        error,
        "Error while deleting event."
      );
    } finally {
      setDeleteBusy(false);
      setDeleteConfirmOpen(false);
    }
  };

  const sortedDescriptions = [...(formData.event_descriptions ?? [])].sort((a, b) => {
    const rankDiff = eventLanguageSortRank(a.language_code) - eventLanguageSortRank(b.language_code);
    if (rankDiff !== 0) return rankDiff;
    return a.language_code.localeCompare(b.language_code);
  });

  const parameterTypes = [
    ' ',
    'Vehicle ID',
    'Point ID',
    'Segment ID',
    'Station ID',
    'Reflector ID',
    'Carrier PLC Status',
    'Order Index',
    'Transport Structure ID',
    'Node ID',
    'CAN Node ID'
  ];

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  useEffect(() => {
    onValidChangeRef.current = onValidChange;
  }, [onValidChange]);

  useEffect(() => {
    const timeoutMs = configuratorMode ? 800 : 300;
    const timeoutId = window.setTimeout(() => {
      const serialized = JSON.stringify(formData);
      if (serialized !== lastSentDataRef.current) {
        lastSentDataRef.current = serialized;
        onDataChangeRef.current?.(formData);
      }
    }, timeoutMs);
    return () => window.clearTimeout(timeoutId);
  }, [formData, configuratorMode]);

  // Debounced validation keeps typing updates out of nested event stacks.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const { valid, errors } = validateEventsForm(formData, { configuratorMode }, isFallingEvent);
      setErrors(prev => (areValidationErrorsEqual(prev, errors) ? prev : errors));
      setAllowSave(prev => (prev === valid ? prev : valid));
      if (lastValidRef.current !== valid) {
        lastValidRef.current = valid;
        onValidChangeRef.current?.(valid);
      }
    }, 120);
    return () => window.clearTimeout(timeoutId);
  }, [formData, configuratorMode, isFallingEvent]);

  const shouldShowSimilarityHints = configuratorMode && createNewEvent && !isFallingEvent;

  const creatorPrimaryLanguage = normalizeEventLanguage(
    formData.attributes?.creator_source_language || "ger"
  ) as EventLanguageCode;

  const handleCreatorPrimaryLanguageChange = useCallback((lang: EventLanguageCode) => {
    setIsDirtyLocal(true);
    setFormData((prev) => ({
      ...prev,
      attributes: {
        ...(prev.attributes ?? {}),
        creator_source_language: lang,
      },
    }));
  }, []);

  const handleOpenSimilarEvent = (eventId: number) => {
    setSimilarDialogEventId(eventId);
    setSimilarDialogOpen(true);
  };

  const handleCloseSimilarEvent = () => {
    setSimilarDialogOpen(false);
    setSimilarDialogEventId(null);
  };

  const requestSimilarEvents = useCallback(async (query: string, lang: string): Promise<SimilarEventHint[]> => {
    const params = new URLSearchParams({
      q: query,
      language: lang,
      limit: "8",
      threshold: "0.35",
    });
    if (formData.event_type) {
      params.set("event_type", String(formData.event_type));
    }
    if (formData.event_contract_name) {
      params.set("event_contract_name", String(formData.event_contract_name));
    }

    const response = await callApi(`${config.apiUrl}/api/events/similar/?${params.toString()}`, "GET");
    return Array.isArray(response?.results) ? response.results : [];
  }, [callApi, formData.event_type, formData.event_contract_name]);


  const { callApi: callApiInner } = useEventsApi();

  const reloadEventFromServer = useCallback(async () => {
    const id = Number(formData.global_event_id);
    if (!id) return;
    setEventLoadBusy(true);
    try {
      const data = await callApiInner(`${config.apiUrl}/api/events/${id}/`, "GET");
      if (data) {
        const merged = mergeWithDefaults(data as Partial<EventsFormData>);
        setFormData(merged);
        resetDirtyBaseline(merged);
        devCommentBaselineRef.current = captureDevCommentBaseline(merged as FullEvent);
        setAlertMessage("Contract name updated.");
        setAlertSeverity("success");
        setAlertOpen(true);
      }
    } catch (err) {
      console.error("Failed to reload event:", err);
    } finally {
      setEventLoadBusy(false);
    }
  }, [formData.global_event_id, callApiInner, resetDirtyBaseline]);

  useEffect(() => {
    if (!eventId) {
      setEventLoadBusy(false);
      return;
    }
    let cancelled = false;
    setEventLoadBusy(true);
    callApiInner(`${config.apiUrl}/api/events/${eventId}/`, "GET")
      .then((data) => {
        if (cancelled || !data) return;
        const merged = mergeWithDefaults(data as Partial<EventsFormData>);
        setFormData(merged);
        resetDirtyBaseline(merged);
        devCommentBaselineRef.current = captureDevCommentBaseline(merged as FullEvent);
      })
      .catch((err) => console.error("Failed to load event:", err))
      .finally(() => {
        if (!cancelled) {
          setEventLoadBusy(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  useEffect(() => {
    const raw = normalizeEventLanguage(formData.attributes?.creator_source_language) || "ger";
    const next = (supportedEventLanguages as string[]).includes(raw) ? (raw as EventLanguageCode) : "ger";
    setRetranslateSourceLang(next);
  }, [formData.global_event_id, formData.attributes?.creator_source_language]);

  useEffect(() => {
    const targetEventId = eventId ?? trackedTranslationEventId ?? null;
    const shouldPoll = Boolean(targetEventId) && (configuratorMode || translationPollActive);
    if (!shouldPoll) return;
    let active = true;
    const poll = async () => {
      try {
        const statusRes = await callApiInner(
          `${config.apiUrl}/api/events/${targetEventId}/translation-status/`,
          "GET"
        );
        if (!active) return;
        setTranslationStatus(statusRes || { status: "idle" });
        const st = (statusRes?.status || "") as string;
        if (
          translationPollActive &&
          !configuratorMode &&
          st === "idle" &&
          (statusRes?.message || "").trim().length > 0
        ) {
          setTranslationPollActive(false);
        }
        if (st === "done" && !translationSynced) {
          const reloaded = await callApiInner(`${config.apiUrl}/api/events/${targetEventId}/`, "GET");
          if (active && reloaded) {
            const merged = mergeWithDefaults(reloaded as Partial<EventsFormData>);
            setFormData(merged);
            resetDirtyBaseline(merged);
            devCommentBaselineRef.current = captureDevCommentBaseline(merged as FullEvent);
            setTranslationSynced(true);
            if (!configuratorMode) {
              setTranslationPollActive(false);
            }
          }
        } else if (st === "error" && !configuratorMode) {
          setTranslationPollActive(false);
        }
      } catch {
        if (active) {
          setTranslationStatus({ status: "error", message: "Status request failed." });
          if (!configuratorMode) {
            setTranslationPollActive(false);
          }
        }
      }
    };
    poll();
    const interval = window.setInterval(poll, 3000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [
    configuratorMode,
    eventId,
    trackedTranslationEventId,
    callApiInner,
    translationSynced,
    translationPollActive,
  ]);

  const handleQueueRetranslation = async (targetLanguages: EventLanguageCode[]) => {
    const gid = formData.global_event_id;
    if (!gid || createNewEvent) return;
    if (targetLanguages.length === 0) {
      setAlertMessage("Select at least one target language.");
      setAlertSeverity("warning");
      setAlertOpen(true);
      return;
    }
    try {
      const lastRunReportedUnchangedSource =
        translationStatus?.status === "done" &&
        String(translationStatus?.message ?? "").toLowerCase().includes("no source changes detected");
      const forceForRequest = retranslateForce || lastRunReportedUnchangedSource;
      setRetranslateBusy(true);
      // Stop polling until translate-now succeeds; otherwise the poll effect can fire before the
      // server clears the previous "done" entry and wrongly treats stale cache as the new run finishing.
      setTranslationPollActive(false);
      setTranslationSynced(false);
      setTranslationStatus({ status: "pending" });
      const queueRes = await callApi(`${config.apiUrl}/api/events/${gid}/translate-now/`, "POST", {
        source_language: retranslateSourceLang,
        force: forceForRequest,
        target_languages: targetLanguages,
      });
      if (queueRes && typeof queueRes === "object" && "status" in queueRes) {
        setTranslationStatus(queueRes as { status: string; message?: string; translated_languages?: string[] });
      }
      setFormData((prev) => ({
        ...prev,
        attributes: {
          ...(prev.attributes ?? {}),
          creator_source_language: retranslateSourceLang,
        },
      }));
      setTranslationPollActive(true);
    } catch {
      setTranslationPollActive(false);
      setTranslationStatus({ status: "error", message: "Failed to queue translation." });
      setAlertMessage(
        "Could not queue translations. You need Creator or Admin (approve) permissions, and Azure translator must be enabled."
      );
      setAlertSeverity("error");
      setAlertOpen(true);
    } finally {
      setRetranslateBusy(false);
    }
  };

  const openRetranslateDialog = () => {
    const next: Partial<Record<EventLanguageCode, boolean>> = {};
    retranslateTargetCandidates.forEach((c) => {
      next[c] = true;
    });
    setRetranslateTargetSelection(next);
    setRetranslateDialogOpen(true);
  };

  const confirmRetranslateFromDialog = () => {
    const selected = retranslateTargetCandidates.filter((c) => retranslateTargetSelection[c]);
    if (selected.length === 0) {
      setAlertMessage("Select at least one target language for Azure translation.");
      setAlertSeverity("warning");
      setAlertOpen(true);
      return;
    }
    setRetranslateDialogOpen(false);
    void handleQueueRetranslation(selected);
  };

  useEffect(() => {
    // nur einmal nach dem ersten Render
    if (isFallingEvent) {
      setFormData(prev => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          severity: "Info",
        },
      }));
    }
  }, []);

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;

  useEffect(() => {
    const serialized = JSON.stringify(formData);
    const nextDirty = serialized !== dirtyBaselineRef.current;
    setIsDirtyLocal((prev) => (prev === nextDirty ? prev : nextDirty));
    onDirtyChangeRef.current?.(nextDirty);
  }, [formData]);

  const theme = useTheme();

  const isNonEmptyString = (v: unknown): v is string =>
    typeof v === "string" &&
    v.trim() !== "" &&
    v.trim().toLowerCase() !== "null" &&
    v.trim().toLowerCase() !== "undefined";

  const isValidDate = (v: unknown): v is Date =>
    v instanceof Date && !Number.isNaN(v.getTime());

  const eventApproved =
    isNonEmptyString(formData?.approved_by) &&
    (isNonEmptyString(formData?.approved_at) || isValidDate(formData?.approved_at as any));
  const disableSaveActions =
    eventLoadBusy || saveBusy || !isDirtyLocal || (createNewEvent && !allowSave);

  /** Rising-edge creator: keep descriptions/parameters accordions closed until severity is chosen. */
  const lockCreatorRisingAccordions =
    configuratorMode &&
    createNewEvent &&
    !isFallingEvent &&
    !nonEmpty(formData.attributes?.severity);

  const creatorRisingAccordionLockTooltip =
    "Select a severity above before you can open this section.";

  const wrapCreatorAccordionLock = (inner: React.ReactElement) =>
    lockCreatorRisingAccordions ? (
      <Tooltip title={creatorRisingAccordionLockTooltip} placement="top-start" arrow>
        {inner}
      </Tooltip>
    ) : (
      inner
    );

  if (eventLoadBusy) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress size={60} thickness={5} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: "2vh", mb: "2vh", overflowAnchor: "none" }}>
      <form
        onSubmit={e => {
          e.preventDefault();
          void handleSubmit({ closeAfterSave: true });
        }}
      >
        <EventBaseInfo
          formData={formData}
          errors={errors}
          isReader={effectiveIsReader}
          isAdmin={isAdmin}
          isCreator={isCreator}
          theme={theme}
          configuratorMode={configuratorMode}
          eventApproved={eventApproved}
          creatingNewResolvingEvent={Boolean(isFallingEvent)}
          handleRisingEventChange={handleRisingEventChange}
          handleAttributeChange={handleAttributeChange}
          handleSelectChange={handleSelectChange}
          handleAttributeSelectChange={handleAttributeSelectChange}
          handleCheckboxChange={handleCheckboxChange}
          onResolveDevComment={() => void handleResolveDevComment()}
          resolveDevCommentBusy={resolveDevCommentBusy}
          creatorRisingContractName={creatorRisingContractName}
          onOpenRename={isAdmin ? () => setContractRenameOpen(true) : undefined}
          renameDisabled={isDirtyLocal}
        />
        {wrapCreatorAccordionLock(
          <Box
            sx={{
              width: "100%",
              display: "flow-root",
              overflowAnchor: "none",
              mt: 3,
            }}
          >
            <Accordion disableGutters disabled={lockCreatorRisingAccordions}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Event Descriptions</Typography>
              </AccordionSummary>

              <AccordionDetails>
            <EventDescriptions
              descriptions={formData.event_descriptions}
              errors={errors}
              isReader={effectiveIsReader}
              isManufacturer={formData.attributes?.manufacturer_event ?? false}
              eventCode={formData.event_code}
              severity={formData.attributes?.severity!}
              isFallingEvent={isFallingEvent}
              onChange={handleDescriptionsChangeRising}
              onRequestSimilarEvents={shouldShowSimilarityHints ? requestSimilarEvents : undefined}
              onOpenSimilarEvent={shouldShowSimilarityHints ? handleOpenSimilarEvent : undefined}
              configuratorMode={configuratorMode}
              translationStatus={translationStatus}
              creatorPrimaryLanguage={creatorPrimaryLanguage}
              onCreatorPrimaryLanguageChange={
                configuratorMode ? handleCreatorPrimaryLanguageChange : undefined
              }
              resolvingRisingHmiHintByLang={formData.attributes?.creator_resolving_rising_hmi_labels}
            />
            {(isAdmin || isCreator) &&
              !effectiveIsReader &&
              !createNewEvent &&
              Boolean(formData.global_event_id) &&
              !formData.attributes?.manufacturer_event && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Azure translations
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Queue Azure translation from the selected source language (saved on the event). You choose
                    which target languages to refresh — uncheck any language you edited manually and want to keep.
                  </Typography>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ sm: "flex-start" }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel id="retranslate-source-lang-label">Source language</InputLabel>
                      <Select<EventLanguageCode>
                        labelId="retranslate-source-lang-label"
                        label="Source language"
                        value={retranslateSourceLang}
                        onChange={(e) =>
                          setRetranslateSourceLang(e.target.value as EventLanguageCode)
                        }
                      >
                        {supportedEventLanguages.map((code) => (
                          <MenuItem key={code} value={code}>
                            {creatorSourceLanguageSelectLabels[code]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ mt: { sm: 0.5 }, ml: { xs: 0, sm: 0 } }}
                    >
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Checkbox
                            checked={retranslateForce}
                            onChange={(e) => setRetranslateForce(e.target.checked)}
                            size="small"
                            disabled={retranslateBusy}
                          />
                        }
                        label="Re-translate even if source text is unchanged"
                      />
                      <Tooltip
                        title={
                          "When checked, the translation request sends force to the server so Azure runs even if " +
                          "the saved source text is considered unchanged since the last run (that pass is usually " +
                          "skipped to save cost). Use when you need another translation round without editing source."
                        }
                        arrow
                        placement="top"
                      >
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            color: "primary.main",
                            cursor: "help",
                            verticalAlign: "middle",
                          }}
                        >
                          <InfoIconMemo fontSize="small" aria-label="About re-translate when unchanged" />
                        </Box>
                      </Tooltip>
                    </Stack>
                    <Tooltip
                      title={
                        isDirtyLocal
                          ? "Save your changes first. Queue translations uses the last saved event on the server — unsaved edits would be lost if you continued."
                          : ""
                      }
                      arrow
                      disableHoverListener={!isDirtyLocal}
                    >
                      <span style={{ display: "inline-block" }}>
                        <Button
                          variant="outlined"
                          disabled={retranslateBusy || isDirtyLocal}
                          onClick={() => openRetranslateDialog()}
                        >
                          {retranslateBusy
                            ? "Queueing…"
                            : isDirtyLocal
                              ? "Save to queue translations…"
                              : "Queue translations…"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Dialog
                    open={retranslateDialogOpen}
                    onClose={() => !retranslateBusy && setRetranslateDialogOpen(false)}
                    maxWidth="xs"
                    fullWidth
                  >
                    <DialogTitle>Translation targets</DialogTitle>
                    <DialogContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select which languages Azure should translate into. Uncheck languages you corrected by hand
                        so they are not overwritten.
                      </Typography>
                      <Stack spacing={0.5}>
                        {retranslateTargetCandidates.map((code) => (
                          <FormControlLabel
                            key={code}
                            control={
                              <Checkbox
                                size="small"
                                checked={Boolean(retranslateTargetSelection[code])}
                                onChange={(e) =>
                                  setRetranslateTargetSelection((prev) => ({
                                    ...prev,
                                    [code]: e.target.checked,
                                  }))
                                }
                                disabled={retranslateBusy}
                              />
                            }
                            label={creatorSourceLanguageSelectLabels[code]}
                          />
                        ))}
                      </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                      <Button onClick={() => setRetranslateDialogOpen(false)} disabled={retranslateBusy}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => confirmRetranslateFromDialog()}
                        disabled={retranslateBusy}
                      >
                        {retranslateBusy ? "Queueing…" : "Queue translations"}
                      </Button>
                    </DialogActions>
                  </Dialog>
                  {!configuratorMode &&
                    translationStatus?.status &&
                    translationStatus.status !== "idle" && (
                      <Typography variant="caption" display="block" sx={{ mt: 1.5 }}>
                        Translation status: {translationStatus.status}
                        {translationStatus?.translated_languages?.length
                          ? ` (${translationStatus.translated_languages.join(", ")})`
                          : ""}
                        {translationStatus?.message ? ` — ${translationStatus.message}` : ""}
                      </Typography>
                    )}
                </Box>
              )}
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {/* Accordion: Event Parameters (unverändert) */}
        {wrapCreatorAccordionLock(
          <Box
            sx={{
              width: "100%",
              display: "flow-root",
              overflowAnchor: "none",
              mt: 3,
            }}
          >
            <Accordion disableGutters disabled={lockCreatorRisingAccordions}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Event Parameters</Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    color: theme.palette.primary.main,
                    ml: 1,
                  }}
                >
                  <Tooltip title="Input parameters passed to the event. Provide additional data the system needs to interpret the event. Parameter Type specifies the type of the passed parameter. If no standard type fits, select type 'None'." arrow>
                    <InfoIconMemo />
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
            <EventParameters
              parameters={formData.attributes?.parameters ?? []}
              isReader={!!effectiveIsReader}
              isManufacturer={!!formData.attributes?.manufacturer_event}
              parameterTypes={[
                " ",
                "Vehicle ID",
                "Point ID",
                "Segment ID",
                "Station ID",
                "Reflector ID",
                "Carrier PLC Status",
                "Order Index",
                "Transport Structure ID",
                "Node ID",
                "CAN Node ID",
              ]}
              onChange={handleParametersChange}
            />
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
        <EventVersionInfo
          formData={formData}
          isAdmin={isAdmin}
          createNewEvent={createNewEvent}
          eventApproved={eventApproved}
          formattedDate={formattedDate}
          formatDateForInput={formatDateForInput}
          handleRisingEventChange={handleRisingEventChange}
        />
        {!createNewEvent &&
          formData.global_event_id > 0 &&
          (isAdmin || isCreator) &&
          !configuratorMode &&
          !formData.attributes?.manufacturer_event && (
            <EventVehicleSoftwareScanAccordion eventId={formData.global_event_id} />
          )}
        {!effectiveIsReader && !configuratorMode && (
          <Box sx={{ mt: 4 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              {!formData.attributes?.manufacturer_event && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={disableSaveActions}
                    startIcon={saveBusy ? <CircularProgress size={20} /> : <ExitToAppIcon />}
                  >
                    {saveBusy ? "Saving..." : "Save & Quit"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={disableSaveActions}
                    onClick={() => void handleSubmit({ closeAfterSave: false })}
                    startIcon={<SaveIcon />}
                  >
                    Save
                  </Button>
                  {!createNewEvent && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      disabled={!isDirtyLocal || saveBusy || eventLoadBusy}
                      onClick={handleUndoChanges}
                      startIcon={<UndoIcon />}
                    >
                      Undo changes
                    </Button>
                  )}
                </>
              )}
            <CustomAlert
              open={alertOpen}
              onClose={() => {
                setAlertOpen(false);
                setAlertDuration(6000);
              }}
              message={alertMessage}
              severity={alertSeverity}
              duration={alertDuration}
            />
            {createNewEvent && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                sx={{ ml: 2 }}
              >
                Reset form
              </Button>
            )}
            {isAdmin && !formData.attributes?.manufacturer_event && !createNewEvent && (
              <ApproveEventButton
                onApprove={handleApprove}
                loading={saveBusy}
                disabled={eventApproved || isDirtyLocal}
                requiresSaveFirst={isDirtyLocal && !eventApproved}
              />
            )}
            {isAdmin &&
              !formData.attributes?.manufacturer_event &&
              !createNewEvent &&
              Boolean(formData.global_event_id) && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={deleteBusy}
                  startIcon={<DeleteOutlineIcon />}
                >
                  Delete this version
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </form>
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this event? This action cannot be undone. Note: only the highest
          version will be removed.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteBusy}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={() => void handleDeleteHighestVersion()}
            disabled={deleteBusy}
            startIcon={deleteBusy ? <CircularProgress size={18} /> : null}
          >
            Delete highest version
          </Button>
        </DialogActions>
      </Dialog>
      {formData.global_event_id > 0 && (
        <ContractRenameDialog
          open={contractRenameOpen}
          globalEventId={formData.global_event_id}
          onClose={() => setContractRenameOpen(false)}
          onApplied={() => void reloadEventFromServer()}
        />
      )}
      <EventsFormDialog
        open={similarDialogOpen}
        onClose={handleCloseSimilarEvent}
        eventId={similarDialogEventId}
        createNewEvent={false}
      />
      <ApiErrorDialog
        content={errorDialog}
        onClose={() => setErrorDialog(null)}
        onUndo={handleUndoChanges}
      />
    </Box>
  );
};
export default EventsForm;